import React, { useState, useRef, useEffect } from 'react';
import { ChildProfile } from '../types';
import { Image as ImageIcon, Upload, Check, PenTool, AlertCircle } from 'lucide-react'; 
import { Loader2 } from './Icons';

interface ChildProfileSetupProps {
  userId: string;
  onSave: (profile: ChildProfile) => Promise<void>;
  existingProfile?: ChildProfile | null;
  error?: string | null;
}

const ChildProfileSetup: React.FC<ChildProfileSetupProps> = ({ userId, onSave, existingProfile, error }) => {
  const [name, setName] = useState(existingProfile?.name || '');
  const [authorName, setAuthorName] = useState(existingProfile?.authorName || '');
  const [age, setAge] = useState<number | ''>(existingProfile?.age || '');
  const [sex, setSex] = useState(existingProfile?.sex || 'prefer not to say');
  const [appearance, setAppearance] = useState(existingProfile?.appearance || '');
  const [photoBase64, setPhotoBase64] = useState(existingProfile?.photoBase64 || '');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (existingProfile) {
      setName(existingProfile.name);
      setAuthorName(existingProfile.authorName || '');
      setAge(existingProfile.age);
      setSex(existingProfile.sex);
      setAppearance(existingProfile.appearance);
      setPhotoBase64(existingProfile.photoBase64);
    }
  }, [existingProfile]);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 20 * 1024 * 1024) {
        alert("Image file is too large. Please choose an image under 20MB.");
        return;
    }

    setIsProcessing(true);
    setLocalError(null);
    const reader = new FileReader();

    reader.onerror = (err) => {
        setIsProcessing(false);
        setLocalError("Photo upload failed. Please try a different image.");
    };

    reader.onload = (event) => {
        const img = new Image();
        img.onerror = (err) => {
            setIsProcessing(false);
            setLocalError("Photo upload failed. Please try a different image.");
        };
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                const MAX_WIDTH = 1280;
                const MAX_HEIGHT = 1280;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width;
                        width = MAX_WIDTH;
                    }
                } else {
                    if (height > MAX_HEIGHT) {
                        width *= MAX_HEIGHT / height;
                        height = MAX_HEIGHT;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0, width, height);
                const compressedBase64 = canvas.toDataURL('image/jpeg', 0.85);
                setPhotoBase64(compressedBase64);
                setIsProcessing(false);
            } catch (err) {
                setIsProcessing(false);
                setLocalError("Photo processing failed. Please try a different image.");
            }
        };
        img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    
    if (!name || !age) {
        setLocalError("Please enter both name and age.");
        return;
    }

    if (isSaving) return;

    setIsSaving(true);
    try {
        const profile: ChildProfile = {
            userId,
            name,
            authorName: authorName.trim() || name,
            age: Number(age),
            sex,
            appearance: appearance.trim() || `A ${age} year old child`, 
            photoBase64: photoBase64 || '' 
        };
        
        await Promise.race([
          onSave(profile),
          new Promise((_, reject) => setTimeout(() => reject(new Error("Save timed out after 15 seconds")), 15000))
        ]);
        
    } catch (err: any) {
        setLocalError(err.message || "Save failed.");
    } finally {
        setIsSaving(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto px-4 py-10 animate-slide-up">
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
            {existingProfile ? 'Update Profile' : 'Who is this story for?'}
        </h2>
        <p className="text-gray-400">
            {existingProfile 
                ? 'Update your child\'s details to keep stories growing with them.' 
                : 'Setup a profile to create personalized stories with your child as the star!'}
        </p>
      </div>

      <form 
        onSubmit={handleSubmit} 
        noValidate 
        className="glass-panel p-8 md:p-10 rounded-3xl shadow-2xl border border-white/5 space-y-8 relative overflow-hidden"
      >
        
        {(error || localError) && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-200 text-sm rounded-xl text-center flex items-center justify-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {localError || error}
            </div>
        )}

        <div className="flex flex-col items-center gap-4">
            <label 
                htmlFor="photo-upload"
                className="w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-gold-500/50 hover:bg-white/5 transition-all relative overflow-hidden group"
            >
                {photoBase64 ? (
                    <img src={photoBase64} alt="Child Preview" className="w-full h-full object-cover" />
                ) : isProcessing ? (
                    <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
                ) : (
                    <div className="flex flex-col items-center text-gray-500 group-hover:text-gold-400">
                        <ImageIcon className="w-8 h-8 mb-2" />
                        <span className="text-xs uppercase font-bold tracking-widest">Select Photo</span>
                    </div>
                )}
            </label>
            <input 
                type="file" 
                id="photo-upload"
                name="photo_upload"
                onChange={handlePhotoUpload} 
                accept="image/*" 
                className="hidden" 
            />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
                <label htmlFor="child_name" className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Child's Name *</label>
                <input
                    type="text"
                    id="child_name"
                    name="child_name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-white focus:border-gold-500 focus:outline-none transition-all"
                    placeholder="e.g. Leo"
                    required
                />
            </div>

            <div className="space-y-2">
                <label htmlFor="age" className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Age *</label>
                <input
                    type="number"
                    id="age"
                    name="age"
                    value={age}
                    onChange={(e) => setAge(e.target.value === '' ? '' : Number(e.target.value))}
                    className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-white focus:border-gold-500 focus:outline-none transition-all"
                    placeholder="e.g. 5"
                    required
                />
            </div>

            <div className="space-y-2">
                <label htmlFor="sex" className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Sex</label>
                <div className="relative">
                    <select
                        id="sex"
                        name="sex"
                        value={sex}
                        onChange={(e) => setSex(e.target.value)}
                        className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-white appearance-none focus:border-gold-500 focus:outline-none transition-all cursor-pointer"
                    >
                        <option value="boy" className="bg-gray-900">boy</option>
                        <option value="girl" className="bg-gray-900">girl</option>
                        <option value="prefer not to say" className="bg-gray-900">prefer not to say</option>
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gold-500 text-[10px]">▼</div>
                </div>
            </div>
        </div>

        <div className="space-y-2">
            <label htmlFor="author_name" className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Author Name</label>
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-gold-400">
                <PenTool className="w-4 h-4" />
              </div>
              <input
                  type="text"
                  id="author_name"
                  name="author_name"
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-xl py-4 pl-12 pr-4 text-white focus:border-gold-500 focus:outline-none transition-all"
                  placeholder="e.g. Mommy & Leo"
              />
            </div>
        </div>

        <div className="space-y-2">
            <label htmlFor="appearance" className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Physical Appearance / Characteristics</label>
            <textarea
                id="appearance"
                name="appearance"
                value={appearance}
                onChange={(e) => setAppearance(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-white focus:border-gold-500 focus:outline-none transition-all h-24 resize-none"
                placeholder="e.g. curly hair, glasses, loves blue shirts..."
            />
        </div>

        <div className="pt-4">
            {isProcessing && (
                <p className="text-center text-xs text-gold-400 animate-pulse mb-3 font-bold uppercase tracking-widest">
                    Processing photo... please wait
                </p>
            )}
            <button
                type="submit"
                disabled={isSaving || isProcessing}
                className="w-full py-5 btn-primary-gradient text-white rounded-2xl font-bold text-xl shadow-xl shadow-forge-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex justify-center items-center gap-3 transform active:scale-[0.99]"
            >
                {isSaving ? (
                    <>
                        <Loader2 className="animate-spin w-6 h-6" />
                        <span>Saving...</span>
                    </>
                ) : (
                    <>
                        <Check className="w-6 h-6" />
                        <span>Save Profile</span>
                    </>
                )}
            </button>
        </div>
      </form>
    </div>
  );
};

export default ChildProfileSetup;